// Runner detail — identity, plans, activity, exports, assistants, sessions,
// support access and the audit trail. Read-only for now: support actions and
// "sign in as runner" arrive in later releases.

import clsx from "clsx";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { AdminRunnerDetail, SupportActionResult } from "@paceroo/domain/views/admin";
import { IconCopy } from "../../components/Icons";
import type { ImpersonationStart } from "@paceroo/domain/views/support";
import { ActionDialog } from "../components/ActionDialog";
import { ImpersonateDialog } from "../components/ImpersonateDialog";
import { AuditDiff } from "../components/AuditDiff";
import { applicableActions, isDanger, type ActionSpec } from "../components/supportActions";
import { EmptyState, ErrorState, LoadingState, Pills, SectionHeader, Tag, statusPills } from "../components/ui";
import { useAdminQuery } from "../lib/api";
import { deviceLabel, fD, fDT, fDY, fRel, fT, firstName, methodLabel, parseWhen } from "../lib/format";
import { useShell } from "../shell/context";
import s from "./Runner.module.css";
import { activityLines, exportGroups, type ActivityGroup } from "./runnerActivity";

const SECTIONS = ["plans", "activity", "exports", "assistants", "sessions", "support", "audit"] as const;
type Section = (typeof SECTIONS)[number];
const SECTION_LABEL: Record<Section, string> = {
  plans: "Plans",
  activity: "Activity",
  exports: "Exports",
  assistants: "Assistants",
  sessions: "Sessions",
  support: "Support",
  audit: "Audit",
};
const GUTTER = { mobile: 16, tablet: 24, desktop: 48 } as const;
const VISIT_END: Record<string, string> = {
  ended: "ended by support",
  expired: "time ran out",
  revoked: "runner revoked access",
  replaced: "replaced by a new session",
  signed_out: "signed out",
};
const ACTIVITY_CHIPS: Array<[ActivityGroup, string]> = [
  ["all", "All"],
  ["auth", "Sign-ins"],
  ["plan", "Plans"],
  ["export", "Exports"],
  ["mcp", "MCP"],
];

export function Runner() {
  const { id = "" } = useParams();
  const { focusSearch } = useShell();
  const q = useAdminQuery<AdminRunnerDetail>(`runners/${encodeURIComponent(id)}`);
  if (q.error?.status === 404)
    return (
      <div className={s.notFound}>
        <Link to="/runners" className={clsx("btn-tertiary", s.back)}>
          ← Runners
        </Link>
        <EmptyState title="No such runner." body={`Nobody has the id ${id}. The account may have been deleted.`} />
      </div>
    );
  if (q.error && !q.data) return <ErrorState page="Runner" error={q.error} onRetry={q.reload} onFind={focusSearch} />;
  if (!q.data || q.data.runner.id !== id) return <LoadingState page="runner" />;
  return <RunnerView d={q.data} reload={q.reload} />;
}

function RunnerView({ d, reload }: { d: AdminRunnerDetail; reload: () => void }) {
  const { bp, toast } = useShell();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ spec: ActionSpec; preselect: string | null } | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [done, setDone] = useState<Partial<Record<string, string>>>({});
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const r = d.runner;
  const now = new Date();
  const [current, setCurrent] = useState<Section>("plans");
  const [activityGroup, setActivityGroup] = useState<ActivityGroup>("all");
  const [allActivity, setAllActivity] = useState(false);
  const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());
  const [openAudit, setOpenAudit] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const refs = useRef<Partial<Record<Section, HTMLElement | null>>>({});

  const actions = applicableActions(d);
  const find = (action: ActionSpec["action"]) => actions.find((a) => a.action === action);
  const open = (spec: ActionSpec | undefined, preselect: string | null = null) => spec && setDialog({ spec, preselect });
  const onDone = (spec: ActionSpec, result: SupportActionResult) => {
    setDialog(null);
    if (result.deleted) {
      toast(`${spec.label} · done for ${firstName(r.name)} · logged in audit`);
      navigate("/runners");
      return;
    }
    setDone((prev) => ({ ...prev, [spec.action]: fT(new Date()) }));
    setFresh((prev) => new Set(prev).add(result.audit.id));
    setOpenAudit(result.audit.id);
    toast(`${spec.label} · done for ${firstName(r.name)} · logged in audit`);
    reload();
  };

  const lines = activityLines(d);
  const shownLines = lines.filter((l) => activityGroup === "all" || l.group === activityGroup);
  const groups = exportGroups(d);
  const exportCount = (d.activity ?? []).filter((e) => e.event === "export.downloaded").length;
  const pills = statusPills({ banned: r.banned, emailVerified: r.emailVerified, supportAccessUntil: d.supportAccess.active?.expiresAt ?? null });
  const counts: Record<Section, string> = {
    plans: String(d.plans.length),
    activity: d.activity ? String(lines.length) : "—",
    exports: d.activity ? String(exportCount) : "—",
    assistants: String(d.assistants.length),
    sessions: String(d.sessions.length),
    support: d.supportAccess.active ? "on" : "",
    audit: String(d.audit.length),
  };

  // Scroll-spy: the section nearest the top of the scroll area is current.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-admin-scroll]");
    if (!root) return;
    const onScroll = () => {
      const top = root.getBoundingClientRect().top + 64;
      let best: Section = "plans";
      for (const sec of SECTIONS) {
        const el = refs.current[sec];
        if (el && el.getBoundingClientRect().top <= top) best = sec;
      }
      setCurrent(best);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => root.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (sec: Section) => {
    const root = document.querySelector<HTMLElement>("[data-admin-scroll]");
    const el = refs.current[sec];
    if (!root || !el) return;
    root.scrollTo({ top: root.scrollTop + el.getBoundingClientRect().top - root.getBoundingClientRect().top - 56, behavior: "smooth" });
  };

  const copyEmail = () => {
    void navigator.clipboard?.writeText(r.email).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const section = (sec: Section, label: ReactNode, body: ReactNode, aside?: ReactNode) => (
    <section ref={(el) => void (refs.current[sec] = el)} className={s.section} data-sec={sec}>
      <SectionHeader label={label}>{aside}</SectionHeader>
      {body}
    </section>
  );

  // Back from a support visit (the app's End sends the browser here with
  // ?support=ended): say how it went, once.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("support") !== "ended") return;
    const last = d.supportAccess.sessions[0];
    if (last?.endedAt) {
      const minutes = Math.max(0, Math.round((parseWhen(last.endedAt).getTime() - parseWhen(last.startedAt).getTime()) / 60_000));
      toast(`Session ended after ${minutes} min · logged · ${firstName(r.name)} can see it in their Profile`);
    }
    setParams({}, { replace: true });
  }, [params, setParams, d.supportAccess.sessions, r.name, toast]);

  const gutter = GUTTER[bp];
  const grant = d.supportAccess.active;
  // Why "Sign in as" can't be used right now, or null when it can.
  const impersonateBlockedBy =
    bp === "mobile"
      ? "Impersonation is desktop-only."
      : r.role === "admin"
        ? "Admin accounts can’t be signed into."
        : r.banned
          ? "Unban the runner first."
          : !grant
            ? "Needs an active grant from the runner. Ask them to turn on Support access in Profile."
            : null;
  const onImpersonationStarted = (result: ImpersonationStart, minutes: number, mode: "read" | "write") => {
    setImpersonating(false);
    setFresh((prev) => new Set(prev).add(result.audit.id));
    setOpenAudit(result.audit.id);
    toast(`Signed in as ${firstName(r.name)} in a new tab · ${minutes}-min ${mode === "write" ? "session with changes" : "read-only session"} · logged in audit`);
    reload();
  };

  return (
    <div className={s.page}>
      <header className={s.header}>
        <Link to="/runners" className={clsx("btn-tertiary", s.back)}>
          ← Runners
        </Link>
        <div className={s.titleRow}>
          <h1 className={clsx("t-serif-lg", s.title)}>
            {r.name}
            <span className={s.accent}>.</span>
          </h1>
          <Pills pills={pills} large />
        </div>
        <dl className={s.facts}>
          <Fact label="Email">
            <span className={clsx("t-mono-sm", s.ellipsis)}>{r.email}</span>
            <button className={clsx("iconbtn", s.copy)} onClick={copyEmail} title={copied ? "Copied" : "Copy"} aria-label="Copy email">
              <IconCopy size={13} />
            </button>
          </Fact>
          <Fact label="Runner id">
            <span className={clsx("t-mono-sm", s.ellipsis)}>{r.id}</span>
          </Fact>
          <Fact label="Sign-in methods">
            <span className="t-body-sm">{r.methods.length ? r.methods.map(methodLabel).join(" · ") : "—"}</span>
          </Fact>
          <Fact label="Email status">
            <span className="t-body-sm">{r.emailVerified ? (r.emailVerifiedAt ? `Verified ${fDY(parseWhen(r.emailVerifiedAt))}` : "Verified") : "Not verified"}</span>
          </Fact>
          <Fact label="Signed up">
            <span className="t-mono-sm">{fDY(parseWhen(r.createdAt))}</span>
          </Fact>
          <Fact label="Last seen">
            <span className="t-mono-sm">{r.lastSeenAt ? `${fRel(parseWhen(r.lastSeenAt), now)} · ${fDT(parseWhen(r.lastSeenAt))}` : "Never"}</span>
          </Fact>
        </dl>
        {r.banned && (
          <div className="ob-callout ob-callout-alert t-body-sm">
            Banned{r.banExpires ? ` until ${fDY(parseWhen(r.banExpires))}` : ""}
            {r.banReason ? ` — ${r.banReason}` : ""}
          </div>
        )}
      </header>

      <nav className={clsx("no-scrollbar", s.index)} style={{ margin: `0 -${gutter}px`, padding: `0 ${gutter}px` }} aria-label="Sections">
        {SECTIONS.map((sec) => (
          <button key={sec} className={clsx("navbtn", s.indexBtn)} data-on={current === sec ? "true" : undefined} onClick={() => jump(sec)}>
            {SECTION_LABEL[sec]}
            {counts[sec] && <span className="t-mono-xs ink-mute">{counts[sec]}</span>}
          </button>
        ))}
      </nav>

      <div className={s.sections}>
        {section(
          "plans",
          "Plans",
          d.plans.length === 0 ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>No plans yet.</p>
          ) : (
            <div className={s.list}>
              {d.plans.map((p) => {
                const end = new Date(parseWhen(p.startAt).getTime() + p.weeks * 7 * 86_400_000);
                return (
                  <div key={p.id} className={s.item}>
                    <div className={s.itemMain}>
                      <div className={s.itemTitle}>
                        <span className={clsx("t-label", s.big)}>{p.name}</span>
                        {p.current ? (
                          <Tag label="Active" color="var(--success)" />
                        ) : p.archivedAt ? (
                          <Tag label={`Archived ${fDY(parseWhen(p.archivedAt))}`} color="var(--ink-mute)" />
                        ) : (
                          <Tag label="Not active" color="var(--ink-mute)" />
                        )}
                      </div>
                      <span className="t-body-sm ink-soft">
                        {[p.templateName, p.methodology, { library: "Library", own: "Own", assistant: "Built by assistant" }[p.source]].filter(Boolean).join(" · ")}
                      </span>
                      <span className="t-mono-xs ink-mute">
                        {fDY(parseWhen(p.startAt))} → {fDY(end)} · race {fDY(parseWhen(p.goalRaceAt))} · {p.weeks} wk
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ),
        )}

        {section(
          "activity",
          "Activity",
          d.activity === null ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>Activity needs Analytics Engine in this environment.</p>
          ) : (
            <>
              <div className={clsx("no-scrollbar", s.chips)}>
                {ACTIVITY_CHIPS.map(([g, label]) => (
                  <button key={g} className={clsx("chip", s.chip)} data-on={activityGroup === g ? "true" : undefined} onClick={() => setActivityGroup(g)}>
                    {label}
                  </button>
                ))}
              </div>
              {shownLines.length === 0 ? (
                <p className={clsx("t-body-sm ink-mute", s.none)}>Nothing in the last 3 months.</p>
              ) : (
                <div className={s.list}>
                  {(allActivity ? shownLines : shownLines.slice(0, 10)).map((l, i) => (
                    <div key={i} className={s.event} style={{ gridTemplateColumns: bp === "mobile" ? "1fr" : "108px 150px 1fr" }}>
                      <span className="t-mono-xs ink-mute">{fDT(l.at)}</span>
                      <span className="t-mono-xs" style={{ color: l.bad ? "var(--alert)" : "var(--ink-soft)" }}>
                        {l.event}
                      </span>
                      <span className="t-body-sm" style={l.bad ? { color: "var(--alert)" } : undefined}>
                        {l.text}
                      </span>
                    </div>
                  ))}
                  {!allActivity && shownLines.length > 10 && (
                    <button className={s.more} onClick={() => setAllActivity(true)}>
                      Show all {shownLines.length} events
                    </button>
                  )}
                </div>
              )}
            </>
          ),
        )}

        {section(
          "exports",
          d.activity ? `Exports · ${exportCount}` : "Exports",
          d.activity === null ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>Exports need Analytics Engine in this environment.</p>
          ) : groups.length === 0 ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>No exports yet.</p>
          ) : (
            <div className={s.groups}>
              {groups.map((g, i) => {
                const open = openGroups.has(i);
                const minutes = Math.round((g.end.getTime() - g.start.getTime()) / 60_000);
                return (
                  <div key={i} className={clsx(s.group, g.flag && s.groupFlag)}>
                    <button
                      className={s.groupBtn}
                      aria-expanded={open}
                      onClick={() =>
                        setOpenGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(i)) next.delete(i);
                          else next.add(i);
                          return next;
                        })
                      }
                    >
                      <span className={s.groupMain}>
                        <span className="t-mono-sm">
                          {fD(g.start)}, {fT(g.start)}
                          {g.items.length > 1 ? `–${fT(g.end)}` : ""}
                        </span>
                        <span className="t-body-sm ink-soft">
                          {g.plan} · {g.viaLink ? "signed link" : "in app"}
                        </span>
                      </span>
                      <span className={s.pips}>
                        {g.pips.map((p) => (
                          <span key={p.tag} className={s.pip}>
                            {p.tag}
                            {p.count > 1 && <span className="ink-mute"> ×{p.count}</span>}
                          </span>
                        ))}
                      </span>
                      <span className={clsx("t-mono-xs", s.summary)} style={{ color: g.flag ? "var(--caution)" : "var(--ink-mute)" }}>
                        {g.items.length > 1 ? `${g.items.length} downloads in ${minutes} min · ${g.pips.length} format${g.pips.length > 1 ? "s" : ""}` : "1 download"}
                      </span>
                    </button>
                    {open && (
                      <div className={s.groupItems}>
                        {g.items.map((it, k) => (
                          <div key={k} className={s.groupItem}>
                            <span className={clsx("t-mono-xs ink-mute", s.time)}>{fT(it.at)}</span>
                            <span className="t-body-sm">{it.format}</span>
                            <span className="t-mono-xs ink-mute">{it.viaLink ? "signed link" : "in app"}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ),
        )}

        {section(
          "assistants",
          "Assistants",
          d.assistants.length === 0 ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>No assistants connected.</p>
          ) : (
            <div className={s.list}>
              {d.assistants.map((a) => (
                <div key={a.clientId} className={s.item}>
                  <div className={s.itemMain}>
                    <span>
                      <span className={clsx("t-label", s.big)}>{a.name ?? "Unnamed app"}</span> <span className="t-mono-xs ink-mute">{a.host ?? a.clientId}</span>
                    </span>
                    <span className="t-body-sm ink-soft">
                      {a.access === "read_write" ? "Can read and change training" : "Can read training"} · connected {fDY(parseWhen(a.connectedAt))}
                      {a.lastActiveAt ? ` · last active ${fRel(parseWhen(a.lastActiveAt), now)}` : ""}
                    </span>
                  </div>
                  <button className={s.alertBtn} onClick={() => open(find("disconnect-app"), a.clientId)}>
                    Disconnect
                  </button>
                </div>
              ))}
            </div>
          ),
        )}

        {section(
          "sessions",
          "Sessions",
          d.sessions.length === 0 ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>Not signed in anywhere.</p>
          ) : (
            <div className={s.list}>
              {d.sessions.map((x, i) => (
                <div key={i} className={clsx(s.item, s.session)}>
                  <span className={clsx("t-label", s.sessionName)}>
                    {deviceLabel(x.userAgent)}
                    {x.impersonatedBy ? " · support session" : ""}
                  </span>
                  <span className="t-mono-xs ink-mute">since {fDY(parseWhen(x.createdAt))}</span>
                  <span className={clsx("t-mono-xs ink-soft", s.lastUsed)}>{fRel(parseWhen(x.updatedAt), now)}</span>
                </div>
              ))}
            </div>
          ),
          d.sessions.length > 0 ? (
            <button className={clsx("btn-secondary", s.smallBtn)} onClick={() => open(find("revoke-sessions"))}>
              Revoke all
            </button>
          ) : undefined,
        )}

        {section(
          "support",
          "Support",
          <>
            <div className={s.list}>
              <div className={s.access}>
                <div className={s.accessMain}>
                  <div className={s.accessHead}>
                    <span className={s.accessDot} style={{ background: grant ? "var(--gold)" : "var(--ink-mute)" }} />
                    <span className={clsx("t-heading-sm", s.accessTitle)}>Support access {grant ? "granted" : "off"}</span>
                  </div>
                  <span className="t-body-sm ink-soft">
                    {grant
                      ? `Granted ${fDT(parseWhen(grant.grantedAt))} · ${grant.scope === "write" ? "changes allowed" : "read-only"}`
                      : d.supportAccess.lastExpiredAt
                        ? `Last grant ended ${fDT(parseWhen(d.supportAccess.lastExpiredAt))}.`
                        : "No grant."}
                  </span>
                  {grant && (
                    <span className="t-mono-xs ink-mute">
                      {Math.max(0, Math.round((parseWhen(grant.expiresAt).getTime() - now.getTime()) / 3_600_000))} h left · expires {fDT(parseWhen(grant.expiresAt))}
                    </span>
                  )}
                </div>
                <div className={s.accessAside}>
                  {impersonateBlockedBy === null ? (
                    <button className="btn-primary" onClick={() => setImpersonating(true)}>
                      Sign in as {firstName(r.name)} →
                    </button>
                  ) : (
                    <>
                      <button className="btn-primary" disabled>
                        Sign in as {firstName(r.name)}
                      </button>
                      <span className={clsx("t-mono-xs ink-mute", s.accessWhy)}>{impersonateBlockedBy}</span>
                    </>
                  )}
                </div>
              </div>
              {d.supportAccess.sessions.map((v) => {
                const live = v.endedAt === null && parseWhen(v.endsAt).getTime() > now.getTime();
                const endAt = parseWhen(v.endedAt ?? v.endsAt);
                return (
                  <div key={v.id} className={clsx(s.item, s.visit)}>
                    <div className={s.itemMain}>
                      <span className="t-label">
                        {fDT(parseWhen(v.startedAt))}–{fT(endAt)} · {v.mode === "write" ? "changes allowed" : "read-only"}
                        {live && <span className={s.liveMark}>● in progress</span>}
                      </span>
                      <span className="t-body-sm ink-mute">
                        “{v.reason}” · {v.adminEmail}
                        {v.mode === "write" ? ` · ${v.changes} change${v.changes === 1 ? "" : "s"}` : ""}
                        {v.endReason && !live ? ` · ${VISIT_END[v.endReason] ?? v.endReason}` : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={s.supportActions}>
              {actions.filter((a) => !isDanger(a.action)).length > 0 && (
                <div className={s.list}>
                  {actions
                    .filter((a) => !isDanger(a.action))
                    .map((a) => (
                      <ActionRow key={a.action} spec={a} doneAt={done[a.action]}>
                        <button className={clsx("btn-secondary", s.rowBtn)} onClick={() => open(a)}>
                          {a.btn}
                        </button>
                      </ActionRow>
                    ))}
                </div>
              )}
              {actions.some((a) => isDanger(a.action)) && (
                <>
                  <SectionHeader label="Danger zone" tone="alert" />
                  <div className={s.list}>
                    {actions
                      .filter((a) => isDanger(a.action))
                      .map((a) => (
                        <ActionRow key={a.action} spec={a} doneAt={done[a.action]}>
                          {bp === "mobile" ? (
                            <span className="t-mono-xs ink-mute">Desktop only</span>
                          ) : (
                            <button className={s.alertBtn} onClick={() => open(a)}>
                              {a.btn}
                            </button>
                          )}
                        </ActionRow>
                      ))}
                  </div>
                </>
              )}
            </div>
          </>,
        )}

        {section(
          "audit",
          "Audit · admin actions on this account",
          d.audit.length === 0 ? (
            <p className={clsx("t-body-sm ink-mute", s.none)}>No admin has acted on this account.</p>
          ) : (
            <div className={s.list}>
              {d.audit.map((a) => (
                <div key={a.id} className={clsx(s.auditRow, fresh.has(a.id) && s.auditFresh)}>
                  <button
                    className={s.auditBtn}
                    style={{ gridTemplateColumns: bp === "mobile" ? "1fr" : "120px 200px 1fr" }}
                    aria-expanded={openAudit === a.id}
                    onClick={() => setOpenAudit(openAudit === a.id ? null : a.id)}
                  >
                    <span className="t-mono-xs ink-mute">
                      {fDT(parseWhen(a.at))}
                      {fresh.has(a.id) && <span className={s.newMark}>● new</span>}
                    </span>
                    <span className="t-label" style={{ color: /Ban|Delete/.test(a.action) ? "var(--alert)" : undefined }}>
                      {a.action}
                    </span>
                    <span className="t-body-sm ink-soft">
                      “{a.reason}” · {a.adminEmail}
                    </span>
                  </button>
                  {openAudit === a.id && <AuditDiff entry={a} />}
                </div>
              ))}
            </div>
          ),
        )}
      </div>
      {dialog && (
        <ActionDialog
          spec={dialog.spec}
          detail={d}
          preselect={dialog.preselect}
          onCancel={() => setDialog(null)}
          onDone={(result) => onDone(dialog.spec, result)}
        />
      )}
      {impersonating && grant && <ImpersonateDialog detail={d} onCancel={() => setImpersonating(false)} onStarted={onImpersonationStarted} />}
    </div>
  );
}

function ActionRow({ spec, doneAt, children }: { spec: ActionSpec; doneAt: string | undefined; children: ReactNode }) {
  return (
    <div className={s.item}>
      <div className={s.itemMain}>
        <span className={clsx("t-label", s.actionLabel)}>{spec.label}</span>
        <span className="t-body-sm ink-mute">{spec.what}</span>
      </div>
      {doneAt && <span className={clsx("t-mono-xs", s.doneMark)}>Done {doneAt} · in audit ↓</span>}
      {children}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={s.fact}>
      <dt className="kicker ink-mute">{label}</dt>
      <dd className={s.factValue}>{children}</dd>
    </div>
  );
}
